'use client';

import { PageRow } from '@components/PageRow';
import { Flex, freddyTheme, Markdown, Typography } from '@coolblue-development/freddy';
import openTeamPageTrigger from '@coolblue-development/ga4-events/triggers/VacancyTextBlock/openTeamPageTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { TextBlock } from '@sharedTypes';
import { useRef } from 'react';

type Props = {
  block: TextBlock;
};

export const Text = ({ block }: Props) => {
  const { title, text } = block;

  const ref = useRef<HTMLDivElement>(null);
  const { trackEvent } = useAnalytics();

  const handleClick = (e: React.MouseEvent<HTMLDivElement | HTMLAnchorElement, MouseEvent>) => {
    const link = (e.target as HTMLElement).closest('a');
    if (!link) return;

    e.preventDefault();
    const links = Array.from(ref.current?.querySelectorAll('a') || []);

    trackEvent(
      openTeamPageTrigger({
        index: links.indexOf(link) + 1,
        total_indices: links.length,
        url: link.href
      })
    );

    window.open(link.href, '_blank');
  };

  return (
    <PageRow withContainer={true}>
      <Flex
        gap={{ xs: 200, md: 600 }}
        direction='column'
        margin={{ md: '0 auto' }}
        sx={theme => ({
          [theme.breakpoint.minWidth('md')]: { maxWidth: '80rem' }
        })}
      >
        <Typography
          variant='h2'
          textAlign={{ md: 'center' }}
          fontSize={{ md: freddyTheme.typography.fontSize['4xl'] }}
        >
          {title}
        </Typography>
        <Markdown
          onClick={handleClick}
          ref={ref}
          sx={{
            '> ul': {
              paddingLeft: freddyTheme.spacing[500],
              '> li': {
                paddingLeft: freddyTheme.spacing[100],
                '&:not(:first-of-type)': {
                  marginTop: freddyTheme.spacing[300]
                },
                '&::marker': {
                  color: freddyTheme.color.font.interactiveBase
                }
              }
            },
            [freddyTheme.breakpoint.minWidth('md')]: {
              'p, > ul': {
                fontSize: freddyTheme.typography.fontSize['2xl'],
                lineHeight: freddyTheme.typography.lineHeight[600]
              },
              '> ul': {
                paddingLeft: freddyTheme.spacing[600],
                '> li': {
                  paddingLeft: freddyTheme.spacing[300]
                }
              }
            }
          }}
        >
          {text}
        </Markdown>
      </Flex>
    </PageRow>
  );
};
